import ReactDOM from "react-dom";
import classes from "../../styles/Modal.module.css";
import { getClasses, fnl } from "../../utils";
import Button from "../buttons/Button";
import Icon, { IconType } from "../Icon";
import { closeModal } from "../../store/slices/modals";
import { useDispatch } from "react-redux";
import {
  useTexts,
  useAppSelector,
  useAnimations,
  useCustomImage,
} from "../../hooks";
import { CSSProperties, useEffect, useState, useRef } from "react";

export interface ModalProps {
  shown: boolean | undefined;
  title?: string;
  icon?: IconType;
  background?: string;
  className?: string;
  name?: string;
  transitional?: boolean;
  unclosable?: boolean;
  onShowStart?: () => void;
  onShowEnd?: () => void;
  onHideStart?: () => void;
  onHideEnd?: () => void;
  onClose?: () => void;
  style?: CSSProperties;
}

const Modal: React.FC<ModalProps> = ({
  shown,
  title,
  icon,
  unclosable,
  background,
  className,
  children,
  onShowStart,
  onShowEnd,
  onHideStart,
  onHideEnd,
  onClose,
}) => {
  const [visible, setVisible] = useState(shown);
  const [initialized, setInitialized] = useState(false);
  const dispatch = useDispatch();
  const closeHandler = () => {
    if (onClose) onClose();
    dispatch(closeModal());
  };
  const { custom } = useTexts();
  const name = useAppSelector((state) => state.details.gameName);
  const gameName = custom.title || name;
  const modalRef = useRef(null);

  const modalIcon = icon ? (
    <Icon type={icon} className={classes.icon} size={40} />
  ) : (
    ""
  );

  const animations = {
    show: {
      duration: 0.3,
      opacity: 1,
      scale: 1,
      ease: "power1.inOut",
      atStart: () => {
        if (onShowStart) onShowStart();
        setVisible(true);
        setInitialized(true);
      },
      atEnd: fnl.bind(onShowEnd),
    },
    hide: {
      duration: 0.3,
      opacity: 0,
      scale: 1.05,
      atStart: fnl.bind(onHideStart),
      atEnd: () => {
        if (onHideEnd) onHideEnd();
        setVisible(false);
      },
    },
  };

  const [animationClass, setAnimation] = useAnimations(
    animations,
    "modal-root",
    modalRef
  );

  useEffect(() => {
    // no "hide" animation is played if the "show" animation wasn't played at least once
    if (!shown && !initialized) return;
    setAnimation(shown ? "show" : "hide");
    // eslint-disable-next-line
  }, [shown]);

  useEffect(() => {
    const handeler = (e: KeyboardEvent) => {
      if (e.code === "Escape" && visible && !unclosable) {
        closeHandler();
      }
    };

    window.addEventListener("keydown", handeler);

    return () => window.removeEventListener("keydown", handeler);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, unclosable]);

  const customBG = useCustomImage(background);
  const style = customBG ? { backgroundImage: `url(${customBG})` } : {};
  const modal = visible ? (
    <div
      ref={modalRef}
      className={getClasses(
        classes,
        className,
        animationClass,
        "root",
        "color text-white"
      )}
    >
      <div className={classes.body}>
        <div className={classes.titlebar}>
          {modalIcon}
          <div className={classes.title}>
            <div className={classes["title-main"]}>{title || ""}</div>
            <div
              className={getClasses(
                classes,
                "title-secondary",
                title ? "" : "empty"
              )}
            >
              {gameName}
            </div>
          </div>
          <Button
            className={classes.close}
            onClick={closeHandler}
            soundEffect="button-click"
            available={true}
            disabled={!!unclosable}
            name={`close-${Date.now()}`}
          >
            <Icon type="Close" size={40} />
          </Button>
        </div>
        <div className={classes.content} style={style}>
          {children}
        </div>
      </div>
    </div>
  ) : (
    ""
  );
  return ReactDOM.createPortal(
    modal,
    document.getElementById("elysium-modal")!
  );
};

export default Modal;
